import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { sum } from 'ramda';
import { useForm } from 'react-hook-form';
import { useMutation, useLazyQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { productFamilyCodes } from 'types/productFamilyCodes';

import { TooltipContainer } from 'pages/VoucherDetails/VoucherDetails.styles';
import useAnimalsPerTrainsQuery from 'components/queries/useAnimalsPerTrainsQuery';
import CanPerform from 'components/shared/CanPerform';
import { ADD_PASSENGERS as ADD_PASSENGERS_SHORTCUT } from 'constants/afterSalesShortcuts';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import filterLegsByProductItemRefs from 'utils/filterLegsByProductItemRefs';
import { getEvacAssistItemRefs, isBookingMultiJourney, isUserEurostarAgent } from 'utils/booking';
import config from 'utils/config';
import { isTravelAgent } from 'utils/user';
import { SP, STD, BP, WC_COMP, ER_STANDARD, STANDARD, COMFORT, PLUS, PREMIUM, PREMIER, EVAC } from 'constants/classOfService';
import { types as paxTypes } from 'types/passengers';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import {
  ANIMALS_ASSISTANCE_TYPE,
  ACCESSIBLE_TRAVEL_TYPE,
  BLOCKED_SEATS_TYPE,
  EVACUATION_CHAIR_TYPE,
  GENERAL_TYPE,
  MAX_NUMBER_OF_NON_WHEELCHAIR_COMPANIONS,
  MAX_NUMBER_PAX_GROUP,
  MAX_NUMBER_PAX_NON_GROUP
} from 'constants/addPassengers';
import Modal from 'components/ui/Modal';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_BOOKING } from 'store/queries';
import { isGroupBooking } from 'utils/isGroupBooking';
import { hasSilverVoucherApplied } from 'utils/silverVouchers';

import { CtaButton, StyledCtaButtons } from '../AfterSalesCtas.styles';
import {
  StyledButtonMenuContainer,
  StyledUpgradeButtonContainer as StyledAddPassengersButtonContainer,
  StyledUpgradeButtonText as StyledAddPassengersButtonText,
  StyledIcon
} from '../UpgradeCta/UpgradeCta.styles';
import {
  runAfterSaleValidator,
  allSegmentsHaveSameSelectedPassengers,
  allSegmentsOfType,
  allSegmentsOfService,
  allSegmentsHaveSameCurrency,
  selectedProductsCheckedIn,
  productHasNotBeenPrrRefunded,
  allSegmentsInOneJourney,
  selectedProductsFamilyAllowedForAddPax,
  hasSnapBooking,
  directionsOfSelectedSegments,
  isDepartureBeforeToday,
  getProductFamiliesOfSelectedTickets
} from '../AfterSalesCtas.utils';
import { ErrorField, StyledForm } from '../../../../ManageClubEurostar/ManageClubEurostar.styles';
import { ActionButton } from '../../../../../components/ui/Button';
import FormattedCurrency from '../../../../../components/ui/FormattedCurrency';
import { GET_JOURNEYS } from '../../../../../store/queries';
import {
  ADD_PASSENGERS,
  PROVISIONALLY_UPDATE_SEAT,
  REVERT_CHANGE,
  UPDATE_EXTRAS,
  UPDATE_PASSENGERS,
  ADD_ADDITIONAL_DETAILS
} from '../../../../../store/mutations';
import { AUTO_ADD_DISCOUNT_CODE_LIST } from '../../../../../types/constants';

import {
  buildPassengers,
  findEarliestLeg,
  getFaresByDirection,
  getSelectedProductFamilyLabels,
  buildSegmentItems,
  EVAC_PASSENGER_TYPE,
  productFamiliesToSearch,
  hasChildrenAlone,
  getGroupPassengerTypeOptions,
  getNonGroupPassengerTypeOptions
} from './AddPassengersCta.utils';

import {
  ClassName,
  ClassPrice,
  ClassSelectionCell,
  ClassSelectionGrid,
  SearchFareTypeText,
  StyledSubmitField,
  StyledActionWrapper
} from './AddPassengersCta.styles';

import NonGroupBookings from './NonGroupBookings';
import GroupBookings from './GroupBookings';
import { bookingStatus } from 'types/booking';
import { PUT_ON_HOLD } from 'store/mutations';

const IS_EVACUATION_CHAIR_ENABLED = config('ENABLE_EVACUATION_CHAIR', { canOverride: true }) === 'true';

const AddPassengersCta = ({
  history: {
    push,
    location: { search }
  },
  label,
  disabled,
  booking,
  selectedItemRefs,
  user
}) => {
  const { register, handleSubmit, formState, watch, getValues, setValue, trigger, reset } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      adults: 0,
      children: 0,
      youths: 0,
      seniors: 0,
      wheelchairCompanions: 0,
      nonWheelchairCompanions: 0,
      wheelchairAdults: 0,
      wheelchairChild: 0,
      guideDogs: 0,
      blockedSeats: 0,
      adultsEvac: 0,
      childrenEvac: 0
    }
  });

  const { errors } = formState;

  const values = watch();
  const [animals, { loading: animalsLoading, data: animalsData }] = useAnimalsPerTrainsQuery();
  const { outbound, inbound, productFamilyGroup } = booking;
  const isContinentalBooking =
    outbound?.some(({ serviceType }) => serviceType?.code === serviceTypeCodes.GREENSPEED) ||
    inbound?.some(({ serviceType }) => serviceType?.code === serviceTypeCodes.GREENSPEED);
  const isGroup = isGroupBooking(productFamilyGroup);
  const ticketsProductFamilies = getProductFamiliesOfSelectedTickets({ booking, selectedItemRefs });
  const hasFIP_ETicketsSelected = ticketsProductFamilies.some((item) => item === productFamilyCodes.FIP_E);
  const isSubmitDisabled = Object.keys(errors).length > 0 || !Object.keys(values).find((key) => !!values[key]);

  const membershipNumber = new URLSearchParams(search).get('membershipNumber');
  const [isButtonMenuOpen, setButtonMenuOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOverMaxPassengers, setIsOverMaxPassengers] = useState(false);
  const [isOverMaxBlockedSeats, setIsOverMaxBlockedSeats] = useState(false);
  const [isOverMaxCompanions, setIsOverMaxCompanions] = useState(false);
  const [isChildrenOnly, setIsChildrenOnly] = useState(false);
  const [passengerList, setPassengerList] = useState([]);
  const [outboundFares, setOutboundFares] = useState([]);
  const [inboundFares, setInboundFares] = useState([]);
  const [showClassSelection, setShowClassSelection] = useState(false);
  const [selectedOutboundFare, setSelectedOutboundFare] = useState(null);
  const [selectedInboundFare, setSelectedInboundFare] = useState(null);
  const [passengersType, setPassengerType] = useState(null);
  const [error, setError] = useState(null);
  const [showMissingTrain, setShowMissingTrain] = useState(false);
  const [showMissingTrainError, setShowMissingTrainError] = useState(false);
  const outboundEarliestLeg = findEarliestLeg(filterLegsByProductItemRefs(outbound, selectedItemRefs));
  const inboundEarliestLeg = findEarliestLeg(filterLegsByProductItemRefs(inbound, selectedItemRefs));

  const redirectToPaxDetails = () =>
    push(
      `/exchange/${booking.reference}/pax?currency=${booking.currency}&addPassengers=true${
        inboundEarliestLeg && selectedInboundFare ? `&InClass=${selectedInboundFare.classOfService.code}` : ''
      }${outboundEarliestLeg && selectedOutboundFare ? `&OutClass=${selectedOutboundFare.classOfService.code}` : ''}${
        membershipNumber ? `&membershipNumber=${membershipNumber}` : ''
      }`
    );

  const showMissingTrainModal = (isInbound, direction) => {
    setShowMissingTrainError({
      isInbound,
      trainNumber: direction.trainNumber,
      date: moment(direction.timing.date).format('DD MMM YYYY')
    });
    setShowMissingTrain(true);
  };

  const [addPassenger, { loading: addPassengerLoading }] = useMutation(ADD_PASSENGERS);
  const [updatePassenger, { loading: updatePassengerLoading }] = useMutation(UPDATE_PASSENGERS);
  const [updateBooking, { loading: updateBookingLoading }] = useMutation(UPDATE_EXTRAS);
  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);
  const [revertChange, { loading: revertChangeLoading }] = useMutation(REVERT_CHANGE, {
    variables: { bookingReference: booking.reference }
  });
  const [provisionallyUpdateSeats, { loading: updateSeatsLoading }] = useMutation(PROVISIONALLY_UPDATE_SEAT);

  const onJourneySearchCompleted = async (journeySearchData) => {
    let newPassengerIds = [];

    if (isChildrenOnly) {
      const newPassengerList = passengerList.filter(({ type }) => type === paxTypes.CHILD);
      newPassengerIds = newPassengerList.map(({ id }) => id);
      setPassengerList(newPassengerList);
    }

    const outboundFareList = getFaresByDirection(
      outboundEarliestLeg,
      journeySearchData.journeySearch.outbound,
      isChildrenOnly,
      newPassengerIds
    );
    const inboundFareList = getFaresByDirection(
      inboundEarliestLeg,
      journeySearchData.journeySearch.inbound,
      isChildrenOnly,
      newPassengerIds
    );

    setOutboundFares(outboundFareList);
    setInboundFares(inboundFareList);

    if (outboundEarliestLeg && !outboundFareList.length) {
      showMissingTrainModal(false, outboundEarliestLeg);

      return;
    }

    if (inboundEarliestLeg && !inboundFareList.length) {
      showMissingTrainModal(true, inboundEarliestLeg);

      return;
    }

    await animals({
      variables: {
        search: {
          outbound: { journeys: outboundFareList.map((fareList) => ({ fares: [fareList] })) },
          inbound: { journeys: inboundFareList.map((fareList) => ({ fares: [fareList] })) }
        },
        outboundDate: outboundFareList?.[0]?.legs?.[0]?.timing?.departureDate,
        inboundDate: inboundFareList?.[0]?.legs?.[0]?.timing?.departureDate,
        hasDogs: journeySearchData.journeySearch.passengers.some((pax) => pax.type === 'DOG')
      }
    });

    setShowClassSelection(true);
  };

  const onUpdateNewPaxSeatsCloseTo = async (newBooking) => {
    const inputSeats = [];

    newBooking.pendingCreationLegs.forEach((newLeg) => {
      newLeg.products.forEach((newProduct) => {
        const existingLeg = newBooking.outbound
          .concat(newBooking.inbound)
          .find((leg) => leg.products.some((l) => l.itemRef === newProduct.itemRef));

        const existingSeat = existingLeg.seats.find((s) => s.itemRef !== newProduct.itemRef);

        inputSeats.push({
          carriage: existingSeat.coach,
          number: existingSeat.number,
          itemRef: Number(newProduct.itemRef),
          strategy: 'close_to'
        });
      });
    });

    try {
      await provisionallyUpdateSeats({
        variables: {
          reference: booking.reference,
          seats: inputSeats
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const [journeySearch, { loading: journeySearchLoading }] = useLazyQuery(GET_JOURNEYS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      onJourneySearchCompleted(data);
    }
  });

  const [getBooking, { loading: getBookingLoading }] = useLazyQuery(GET_BOOKING, {
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      await onUpdateNewPaxSeatsCloseTo(data.booking);
      redirectToPaxDetails();
    },
    onError: () => {
      redirectToPaxDetails();
    }
  });

  const isLoading = journeySearchLoading || animalsLoading.outbound || animalsLoading.inbound;
  const hasSnapTickets = !isGroup && selectedItemRefs.length > 0 && runAfterSaleValidator(booking, selectedItemRefs)([hasSnapBooking]);
  const isDutyTollFree = !isGroup && selectedItemRefs.length > 0 && booking.productFamilyGroup === productFamilyCodes.DUTY_NON_TOLLABLE;
  const { outboundLabel, inboundLabel } = getSelectedProductFamilyLabels({ booking, selectedItemRefs });
  const currentPassengers = booking.passengers.filter((pax) => booking.allBookingItems.find((abi) => abi.passengerId === pax.id));
  const numberOfNonWheelchairCompanions = currentPassengers.filter((p) => p.disabilityType === paxTypes.NO_WHEELCHAIR_COMPANION).length;
  const numberOfWheelchairUsers = currentPassengers.filter((p) => p.disabilityType === paxTypes.WHEELCHAIR).length;
  const numberOfWheelchairCompanions = currentPassengers.filter((p) => p.disabilityType === paxTypes.COMPANION).length;
  const hasWheelchairPassenger = numberOfWheelchairUsers > 0;
  const hasWheelchairCompanion = numberOfWheelchairCompanions > 0;
  const hasNonWheelchairCompanion = numberOfNonWheelchairCompanions > 0;
  const bookingHasAnimals = currentPassengers.some(({ type }) => type === paxTypes.DOG);
  const isDogAssistanceOnly = currentPassengers.every((p) => p.type === paxTypes.DOG);
  const hasActiveSilverVoucher = hasSilverVoucherApplied(booking.vouchers, booking.payments);

  const passengerWithDiscountCard = currentPassengers.find(
    (passenger) =>
      passenger.discountCards &&
      passenger.discountCards.length > 0 &&
      passenger.discountCards.some((card) => AUTO_ADD_DISCOUNT_CODE_LIST.includes(card.code))
  );

  const hasSelectedItems = selectedItemRefs.length > 0;
  const samePaxSingleEurostarWithValidPFAndCurrency = runAfterSaleValidator(
    booking,
    selectedItemRefs
  )([
    allSegmentsHaveSameSelectedPassengers,
    allSegmentsInOneJourney,
    allSegmentsOfType([STD, ER_STANDARD, STANDARD, SP, COMFORT, PLUS, BP, PREMIUM, PREMIER, WC_COMP, EVAC]),
    allSegmentsOfService([serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED]),
    allSegmentsHaveSameCurrency,
    selectedProductsFamilyAllowedForAddPax,
    productHasNotBeenPrrRefunded
  ]);
  const productsNotCheckedIn = !runAfterSaleValidator(booking, selectedItemRefs)([selectedProductsCheckedIn('some')]);
  const isEurostarOrTravelAgent = isUserEurostarAgent(user) || isTravelAgent(user);
  const notMultiCurrencyBooking = !isBookingMultiJourney(booking?.additionalDetails);
  const nonHotelAndPackageBooking = !booking.isPackage && !booking.hotels?.length;

  const isAddPassengerPossible =
    hasSelectedItems &&
    samePaxSingleEurostarWithValidPFAndCurrency &&
    productsNotCheckedIn &&
    isEurostarOrTravelAgent &&
    notMultiCurrencyBooking &&
    !isDogAssistanceOnly &&
    !hasActiveSilverVoucher &&
    nonHotelAndPackageBooking &&
    !isDepartureBeforeToday(selectedItemRefs, booking);

  const isDisabled = disabled || !isAddPassengerPossible;
  const isUserTravelAgent = isTravelAgent(user);
  const passengerTypeOptions = isGroup
    ? getGroupPassengerTypeOptions({
        bookingHasAnimals,
        isContinentalBooking,
        isTravelAgent: isUserTravelAgent,
        hasSnapTickets,
        hasWheelchairPassenger,
        hasWheelchairCompanion,
        hasNonWheelchairCompanion,
        isLoyaltyBooking: booking.isLoyalty,
        isDutyTollFree
      })
    : getNonGroupPassengerTypeOptions({
        bookingHasAnimals,
        hasSnapTickets,
        hasWheelchairPassenger,
        hasWheelchairCompanion,
        hasNonWheelchairCompanion,
        isContinentalBooking,
        isLoyaltyBooking: booking.isLoyalty,
        isDutyTollFree,
        isTravelAgent: isUserTravelAgent,
        isEvacuationChairEnabled: IS_EVACUATION_CHAIR_ENABLED
      });
  const addPassengerTypeButtons = [
    {
      dataCy: 'general-select',
      dataTestId: 'general-select',
      isVisible: passengerTypeOptions.canShowGeneral,
      label: <FormattedMessage id="addPassengers.general" />,
      passengersType: GENERAL_TYPE
    },
    {
      dataCy: 'special-assistance-select',
      dataTestId: 'special-assistance-select',
      isVisible: passengerTypeOptions.canShowSpecialAssistance,
      label: <FormattedMessage id="addPassengers.specialAssistance" />,
      passengersType: ACCESSIBLE_TRAVEL_TYPE
    },
    {
      dataCy: 'animals-assistance-select',
      dataTestId: 'animals-assistance-select',
      disabled: passengerTypeOptions.isAnimalsDisabled || passengerTypeOptions.shouldShowAnimalsAlreadyBooked,
      isVisible: passengerTypeOptions.canShowAnimals || passengerTypeOptions.shouldShowAnimalsAlreadyBooked,
      label: <FormattedMessage id="addAssistance.assistanceAnimal" />,
      passengersType: ANIMALS_ASSISTANCE_TYPE
    },
    {
      dataCy: 'blocked-seats-select',
      dataTestId: 'blocked-seats-select',
      isVisible: passengerTypeOptions.canShowBlockedSeats,
      label: <FormattedMessage id="addPassengers.blockedSeats" />,
      passengersType: BLOCKED_SEATS_TYPE
    },
    {
      dataCy: 'evacuation-chair-select',
      dataTestId: 'evacuation-chair-select',
      isVisible: !isGroup && passengerTypeOptions.canShowEvacuationChair,
      label: <FormattedMessage id="wheelchairSearch.answers.evac" />,
      passengersType: EVACUATION_CHAIR_TYPE
    }
  ].filter(({ isVisible }) => isVisible);
  const hasEnabledAccordionButton = addPassengerTypeButtons.some(({ disabled: isButtonDisabled }) => !isButtonDisabled);
  const isAccordionDisabled = isDisabled || !hasEnabledAccordionButton;

  const handleToggleButtonMenu = () => setButtonMenuOpen((isMenuOpen) => !isMenuOpen);

  const handleOpenAddPassengersModal = (selectedPassengersType) => {
    setPassengerType(selectedPassengersType);
    setButtonMenuOpen(false);
    setIsOpen(true);
  };

  const onSubmitSearch = (formValues, searchForPub = false) => {
    let shouldBuildChildrenOnlyPassengers = false;

    const currentPassengersNo = booking.passengers.filter((pax) =>
      booking.allBookingItems.find((abi) => abi.passengerId === pax.id && abi.productFamily.familyGroup !== BLOCKED_SEATS_TYPE)
    ).length;

    const addPassengersDirectionsSegments = directionsOfSelectedSegments({ selectedItemRefs, booking });
    const numberOfBlockedSeatsPerDirection = {
      outbound:
        booking.outbound
          ?.flatMap(
            ({ activeBookingItems }) =>
              activeBookingItems.filter(({ productFamily }) => productFamily.familyGroup === BLOCKED_SEATS_TYPE)?.length
          )
          ?.reduce((a, v) => a + v, 0) || 0,
      inbound:
        booking.inbound
          ?.flatMap(
            ({ activeBookingItems }) =>
              activeBookingItems.filter(({ productFamily }) => productFamily.familyGroup === BLOCKED_SEATS_TYPE)?.length
          )
          ?.reduce((a, v) => a + v, 0) || 0
    };

    const numberOfBlockedSeatsInSegment =
      addPassengersDirectionsSegments.length > 1
        ? Math.max(numberOfBlockedSeatsPerDirection.outbound, numberOfBlockedSeatsPerDirection.inbound)
        : numberOfBlockedSeatsPerDirection[addPassengersDirectionsSegments[0]];

    const passengersByType = {
      adults: Number(formValues.adults || 0),
      children: Number(formValues.children || 0),
      youths: Number(formValues.youths || 0),
      seniors: Number(formValues.seniors || 0),
      wheelchairCompanions: Number(formValues.wheelchairCompanions || 0),
      nonWheelchairCompanions: Number(formValues.nonWheelchairCompanions || 0),
      wheelchairAdults: Number(formValues.wheelchairAdults || 0),
      wheelchairChild: Number(formValues.wheelchairChild || 0),
      guideDogs: Number(formValues.guideDogs || 0),
      blockedSeats: Number(formValues.blockedSeats || 0),
      adultsEvac: Number(formValues.adultsEvac || 0),
      childrenEvac: Number(formValues.childrenEvac || 0)
    };
    const totalNo =
      currentPassengersNo +
      passengersByType.adults +
      passengersByType.children +
      passengersByType.youths +
      passengersByType.seniors +
      passengersByType.wheelchairCompanions +
      passengersByType.nonWheelchairCompanions +
      passengersByType.wheelchairAdults +
      passengersByType.wheelchairChild +
      passengersByType.guideDogs +
      passengersByType.blockedSeats +
      passengersByType.adultsEvac +
      passengersByType.childrenEvac;

    if ((booking.isGroupBooking && totalNo > MAX_NUMBER_PAX_GROUP) || (!booking.isGroupBooking && totalNo > MAX_NUMBER_PAX_NON_GROUP)) {
      setIsOverMaxPassengers(true);

      return;
    }

    if (passengersByType.blockedSeats > 0 && passengersByType.blockedSeats + numberOfBlockedSeatsInSegment > currentPassengersNo) {
      setIsOverMaxBlockedSeats(true);
      return;
    }

    if (
      passengersByType.nonWheelchairCompanions &&
      numberOfNonWheelchairCompanions + passengersByType.nonWheelchairCompanions > MAX_NUMBER_OF_NON_WHEELCHAIR_COMPANIONS
    ) {
      setIsOverMaxCompanions(true);
      return;
    }

    if (
      !booking.isGroupBooking &&
      booking.passengers.every(({ type }) => [paxTypes.CHILD, paxTypes.YOUTH, paxTypes.ADULT, paxTypes.SENIOR].includes(type)) &&
      totalNo - currentPassengersNo === passengersByType.children
    ) {
      shouldBuildChildrenOnlyPassengers = true;
      setIsChildrenOnly(true);
    }

    const passengers = buildPassengers({
      booking,
      shouldBuildChildrenOnlyPassengers,
      adults: passengersByType.blockedSeats > 0 ? passengersByType.blockedSeats : passengersByType.adults,
      children: passengersByType.children,
      youths: passengersByType.youths,
      seniors: passengersByType.seniors,
      wheelchairCompanions: passengersByType.wheelchairCompanions,
      nonWheelchairCompanions: passengersByType.nonWheelchairCompanions,
      wheelchairAdults: passengersByType.wheelchairAdults,
      wheelchairChild: passengersByType.wheelchairChild,
      guideDogs: passengersByType.guideDogs,
      adultsEvac: passengersByType.adultsEvac,
      childrenEvac: passengersByType.childrenEvac
    });

    setPassengerList(passengers);
    const contractCode = booking?.agent?.contractCode;

    const variables = {
      productFamilies: productFamiliesToSearch({ passengersByType, searchForPub, booking }),
      currency: booking.currency,
      passengers,
      isYouthAdultConversionActive: false,
      ...(Boolean(contractCode) && { contractCode }),
      ...(isGroupBooking(booking.productFamily) && { maxTransfers: 0 }),
      useNewLoyaltySystem: true
    };

    if (outboundEarliestLeg) {
      variables.origin = outboundEarliestLeg.origin.uic;
      variables.destination = outboundEarliestLeg.addOnLeg
        ? outboundEarliestLeg.addOnLeg.destination.uic
        : outboundEarliestLeg.destination.uic;
      variables.outboundDate = outboundEarliestLeg.timing.date;
    }

    if (inboundEarliestLeg) {
      variables.inboundDate = inboundEarliestLeg.timing.date;
      variables.returningOrigin = inboundEarliestLeg.addOnLeg ? inboundEarliestLeg.addOnLeg.origin.uic : inboundEarliestLeg.origin.uic;
      variables.returningDestination = inboundEarliestLeg.destination.uic;

      if (!outboundEarliestLeg) {
        variables.origin = inboundEarliestLeg.addOnLeg ? inboundEarliestLeg.addOnLeg.origin.uic : inboundEarliestLeg.origin.uic;
        variables.destination = inboundEarliestLeg.destination.uic;
      }
    }

    journeySearch({
      variables
    });
  };

  const showFareSelection = (fares, animals, isInbound) => {
    const faresLabel = `addPassengers.${isInbound ? 'inbound' : 'outbound'}`;
    let classesOfService = fares.map(({ classOfService }) => classOfService);

    const animalsAssistance = !!animals;
    const checkAnimals = animalsAssistance ? animals?.total <= 3 : true;

    if (
      (isGroup && passengersType === ACCESSIBLE_TRAVEL_TYPE && values.nonWheelchairCompanions === 0) ||
      (!isGroup && hasWheelchairPassenger && !hasWheelchairCompanion && !animalsAssistance)
    ) {
      classesOfService = classesOfService.filter(({ code }) => code === WC_COMP);
    }

    return (
      <>
        <FormattedMessage id={faresLabel} />
        <ClassSelectionGrid>
          {classesOfService.map(({ code, name }) => {
            const fare = fares.find((f) => f.classOfService.code === code);
            const selectedFare = isInbound ? selectedInboundFare : selectedOutboundFare;
            const isSelected = selectedFare && selectedFare.classOfService.code === code;

            return (
              <ClassSelectionCell
                key={code}
                isDisabled={!fare || animals?.total >= 4}
                isSelected={isSelected}
                onClick={() => fare && checkAnimals && (isInbound ? setSelectedInboundFare(fare) : setSelectedOutboundFare(fare))}
                data-for={animals?.total >= 4 ? 'animals-tooltip' : ''}
                data-tip
              >
                <ClassName>{name}</ClassName>
                <ClassPrice>
                  {fare && <FormattedCurrency currency={booking.currency} amount={sum(fare?.bookingItems?.map((bi) => bi.price))} />}
                  {!fare && <div>N/A</div>}
                </ClassPrice>
                {animals?.total >= 4 && (
                  <ReactTooltip className="tooltip" id="animals-tooltip" data-cy="animals-tooltip" place="bottom">
                    <TooltipContainer>
                      <FormattedMessage
                        id="assistanceAnimals.availabilitiesMessage"
                        values={{
                          available: Math.max(4 - animals?.total, 0),
                          booked: animals?.total
                        }}
                      />{' '}
                      <FormattedMessage
                        id="assistanceAnimals.availabilitiesMessageCoaches"
                        values={{
                          coaches: animals?.carriages.map(({ carriage }) => carriage)
                        }}
                      />
                    </TooltipContainer>
                  </ReactTooltip>
                )}
              </ClassSelectionCell>
            );
          })}
        </ClassSelectionGrid>
        <br />
      </>
    );
  };

  const toggleIsOpenModal = () => {
    setSelectedOutboundFare(null);
    setSelectedInboundFare(null);
    setPassengerType(null);
    setOutboundFares([]);
    setInboundFares([]);
    setPassengerList([]);
    setShowClassSelection(false);
    setIsOverMaxPassengers(false);
    setIsOverMaxBlockedSeats(false);
    setIsOverMaxCompanions(false);
    setIsChildrenOnly(false);
    reset();
    setButtonMenuOpen(false);
    setIsOpen(!isOpen);
  };

  const toggleMissingTrainModal = () => setShowMissingTrain(!showMissingTrain);

  const buildSegments = (fare, isInbound, passengersType) => {
    const leg = fare.legs[0];

    return {
      direction: isInbound ? 'inbound' : 'outbound',
      origin: leg.origin.uic,
      destination: leg.destination.uic,
      serviceName: leg.serviceName,
      serviceIdentifier: leg.serviceIdentifier,
      startValidityDate: leg.timing.departureDate,
      items: buildSegmentItems({ bookingItems: fare.bookingItems, passengersType })
    };
  };

  const [putOnHold] = useMutation(PUT_ON_HOLD, {
    variables: { bookingReference: booking.reference }
  });

  const addPassengersToSegments = async () => {
    try {
      await addPassenger({
        variables: {
          bookingReference: booking.reference,
          passengers: passengerList.map((p) => ({
            type: p.type,
            disabilityType: p.disabilityType
          }))
        }
      });

      if (passengerWithDiscountCard) {
        await updatePassenger({
          variables: {
            bookingReference: booking.reference,
            passengers: passengerList.map((p) => ({
              id: p.id,
              ...(passengerWithDiscountCard && {
                discountCards: passengerWithDiscountCard.discountCards
                  .filter((card) => AUTO_ADD_DISCOUNT_CODE_LIST.includes(card.code))
                  .map(({ name, code, requiresPassengerIdentifier }) => ({
                    name,
                    code,
                    requiresPassengerIdentifier
                  }))
              })
            }))
          }
        });
      }

      const segments = [];

      if (outboundEarliestLeg) {
        segments.push(buildSegments(selectedOutboundFare, false, passengersType));
      }

      if (inboundEarliestLeg) {
        segments.push(buildSegments(selectedInboundFare, true, passengersType));
      }

      try {
        const {
          data: { updateBookingExtras }
        } = await updateBooking({
          variables: {
            currency: booking.currency,
            bookingReference: booking.reference,
            segments
          }
        });

        if (passengersType === EVAC_PASSENGER_TYPE) {
          const bookingExtras = getEvacAssistItemRefs([...updateBookingExtras.outboundExtras, ...updateBookingExtras.inboundExtras]);

          await addAdditionalDetails({
            variables: {
              bookingReference: updateBookingExtras.reference,
              additionalDetails: bookingExtras.map((item) => ({
                key: 'MAAS',
                value: 'Evac chair',
                itemRefId: Number(item)
              }))
            }
          });
        }

        getBooking({
          variables: { reference: booking.reference }
        });
      } catch (err) {
        await revertChange();
        toggleIsOpenModal();
        setError(err);
      }

      if (booking.status === bookingStatus.ON_HOLD) {
        putOnHold();
      }
    } catch (err) {
      toggleIsOpenModal();
      setError(err);
    }
  };

  if (error) {
    throw new AgentPortalError(error);
  }

  const isAddPassengersToSegmentsDisabled = (outboundEarliestLeg && !selectedOutboundFare) || (inboundEarliestLeg && !selectedInboundFare);
  const searchForPubTickets = passengersType === GENERAL_TYPE && hasChildrenAlone({ values }) && hasFIP_ETicketsSelected;

  const actionComponent = (
    <StyledSubmitField>
      <ActionButton
        data-cy="search-trains"
        onClick={handleSubmit((values) => onSubmitSearch(values))}
        loading={isLoading}
        disabled={isSubmitDisabled || isLoading}
      >
        <FormattedMessage id="addPassengers.searchTrains" />
      </ActionButton>
      {searchForPubTickets ? (
        <StyledActionWrapper>
          <ActionButton
            data-cy="search-trains-pub-tickets"
            onClick={handleSubmit((values) => onSubmitSearch(values, searchForPubTickets))}
            loading={isLoading}
            disabled={isSubmitDisabled || isLoading}
          >
            <FormattedMessage id="addPassengers.searchPublicTickets" />
          </ActionButton>
        </StyledActionWrapper>
      ) : null}
    </StyledSubmitField>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={toggleIsOpenModal} title={<FormattedMessage id="addPassengers.searchTrains" />}>
        {!showClassSelection && (
          <StyledForm>
            {isOverMaxPassengers && (
              <ErrorField id="error-over-max-passengers">
                <FormattedMessage id={`addPassengers.maxPassengers${booking.isGroupBooking ? 'Group' : ''}Error`} />
              </ErrorField>
            )}
            {isOverMaxBlockedSeats && (
              <ErrorField id="error-over-max-blocked-seats">
                <FormattedMessage id={`addPassengers.maxBlockedSeatsError`} />
              </ErrorField>
            )}
            {isOverMaxCompanions && (
              <ErrorField id="error-over-max-companions">
                <FormattedMessage id="addPassengers.maxCompanionsError" />
              </ErrorField>
            )}
            {(outboundLabel || inboundLabel) && (
              <SearchFareTypeText data-testid="search-trains-fare-type">
                {outboundLabel && (
                  <div>
                    <FormattedMessage
                      id="addPassengers.searchTrainsFareTypeDirection"
                      values={{ direction: <FormattedMessage id="addPassengers.outbound" />, value: outboundLabel }}
                    />
                  </div>
                )}
                {inboundLabel && (
                  <div>
                    <FormattedMessage
                      id="addPassengers.searchTrainsFareTypeDirection"
                      values={{ direction: <FormattedMessage id="addPassengers.inbound" />, value: inboundLabel }}
                    />
                  </div>
                )}
              </SearchFareTypeText>
            )}
            {isGroup ? (
              <GroupBookings
                register={register}
                getValues={getValues}
                passengersType={passengersType}
                setPassengerType={setPassengerType}
                errors={errors}
                trigger={trigger}
                hasSnapTickets={hasSnapTickets}
                hasWheelchairPassenger={hasWheelchairPassenger}
                hasWheelchairCompanion={hasWheelchairCompanion}
                hasNonWheelchairCompanion={hasNonWheelchairCompanion}
                numberOfWheelchairUsers={numberOfWheelchairUsers}
                numberOfWheelchairCompanions={numberOfWheelchairCompanions}
                numberOfNonWheelchairCompanions={numberOfNonWheelchairCompanions}
                actionComponent={actionComponent}
                bookingHasAnimals={bookingHasAnimals}
                isContinentalBooking={isContinentalBooking}
                isLoyaltyBooking={booking.isLoyalty}
                isDutyTollFree={isDutyTollFree}
                isTravelAgent={isUserTravelAgent}
                setValue={setValue}
              />
            ) : (
              <NonGroupBookings
                bookingHasAnimals={bookingHasAnimals}
                passengersType={passengersType}
                setPassengerType={setPassengerType}
                hasSnapTickets={hasSnapTickets}
                isDutyTollFree={isDutyTollFree}
                register={register}
                errors={errors}
                hasWheelchairPassenger={hasWheelchairPassenger}
                hasWheelchairCompanion={hasWheelchairCompanion}
                hasNonWheelchairCompanion={hasNonWheelchairCompanion}
                actionComponent={actionComponent}
                isContinentalBooking={isContinentalBooking}
                isLoyaltyBooking={booking.isLoyalty}
                isTravelAgent={isUserTravelAgent}
                getValues={getValues}
              />
            )}
          </StyledForm>
        )}
        {showClassSelection && outboundEarliestLeg && showFareSelection(outboundFares, animalsData?.outbound?.[0], false)}
        {showClassSelection && inboundEarliestLeg && showFareSelection(inboundFares, animalsData?.inbound?.[0], true)}
        {showClassSelection && (
          <ActionButton
            data-cy="add-passengers"
            loading={
              isLoading ||
              addPassengerLoading ||
              updateBookingLoading ||
              revertChangeLoading ||
              getBookingLoading ||
              updateSeatsLoading ||
              addAdditionalDetailsLoading ||
              updatePassengerLoading
            }
            disabled={
              isAddPassengersToSegmentsDisabled ||
              isLoading ||
              addPassengerLoading ||
              updateBookingLoading ||
              addAdditionalDetailsLoading ||
              revertChangeLoading ||
              getBookingLoading ||
              updateSeatsLoading
            }
            onClick={addPassengersToSegments}
          >
            <FormattedMessage id="addPassengers.addPassengers" />
          </ActionButton>
        )}
      </Modal>

      <Modal isOpen={showMissingTrain} onClose={toggleMissingTrainModal}>
        <FormattedMessage
          id={`addPassengers.${showMissingTrainError.isInbound ? 'missingTrainInbound' : 'missingTrainOutbound'}`}
          values={{
            trainNumber: showMissingTrainError.trainNumber,
            date: showMissingTrainError.date
          }}
        />
      </Modal>
      <CanPerform afterSalesShortcut={ADD_PASSENGERS_SHORTCUT}>
        {(isEligible) =>
          isEligible && (
            <>
              <CtaButton
                isMenuOpen={isButtonMenuOpen}
                type="button"
                data-cy="add-passengers-cta"
                disabled={isAccordionDisabled}
                label={
                  <StyledAddPassengersButtonContainer isDisabled={isAccordionDisabled} isMenuOpen={isButtonMenuOpen}>
                    <StyledAddPassengersButtonText isDisabled={isAccordionDisabled}>{label}</StyledAddPassengersButtonText>
                    <StyledIcon isDisabled={isAccordionDisabled} isMenuOpen={isButtonMenuOpen} />
                  </StyledAddPassengersButtonContainer>
                }
                onClick={handleToggleButtonMenu}
              />
              <StyledButtonMenuContainer isMenuOpen={isButtonMenuOpen}>
                {addPassengerTypeButtons.map(
                  ({ dataCy, dataTestId, disabled: isButtonDisabled, label: buttonLabel, passengersType: buttonPassengersType }) => (
                    <StyledCtaButtons
                      key={dataCy}
                      data-cy={dataCy}
                      data-testid={dataTestId}
                      label={buttonLabel}
                      disabled={isButtonDisabled}
                      onClick={() => handleOpenAddPassengersModal(buttonPassengersType)}
                      isMenuOpen={isButtonMenuOpen}
                    />
                  )
                )}
              </StyledButtonMenuContainer>
            </>
          )
        }
      </CanPerform>
    </>
  );
};

AddPassengersCta.propTypes = {
  user: userShape,
  booking: bookingShape,
  history: historyShape,
  label: PropTypes.element,
  disabled: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string)
};

export default AddPassengersCta;
